import { useEffect, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";

import { clickableText, cx } from "@/lib/tailwind-utils";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./collapsible";
import Tooltip from "./tooltip";

interface CollapsibleTextProps {
  text: string;
  collapsedText?: string;
  open?: boolean;
  children: React.ReactNode;
  className?: string;
  textClassName?: string;
  onExpandChange?: (expanded: boolean) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  tooltip?: string;
  stopPropagation?: boolean;
}

const CollapsibleText = ({
  text,
  collapsedText,
  children,
  className,
  textClassName,
  open = false,
  onExpandChange,
  disabled = false,
  stopPropagation = false,
  tooltip,
  icon,
}: CollapsibleTextProps) => {
  const [isExpanded, setIsExpanded] = useState(open);

  useEffect(() => {
    setIsExpanded(open);
  }, [open]);

  const handleExpandToggle = (expanded: boolean) => {
    setIsExpanded(expanded);
    onExpandChange?.(expanded);
  };

  if (disabled) {
    return (
      <div className={className}>
        <p
          onClick={(e) => {
            if (stopPropagation) {
              e.stopPropagation();
            }
          }}
          className={cx(
            textClassName,
            clickableText,
            "pointer-events-none w-fit font-semibold text-foreground-muted grayscale",
          )}
        >
          <FiChevronRight className="mr-1 inline size-3.5" />
          {icon} {text}
          {tooltip && (
            <Tooltip content={tooltip}>
              <FaInfoCircle className="ml-1 size-3" />
            </Tooltip>
          )}
        </p>
      </div>
    );
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={handleExpandToggle}
      className={className}
    >
      <CollapsibleTrigger
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
        }}
        className={cx(clickableText, textClassName, "flex w-fit items-center")}
      >
        <FiChevronRight
          className={cx(
            "mr-1 size-3.5 transition-transform duration-200",
            isExpanded && "rotate-90",
          )}
        />
        {icon} {text}
        {tooltip && (
          <Tooltip content={tooltip}>
            <FaInfoCircle className="ml-1 size-3" />
          </Tooltip>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        {children}
        {collapsedText && (
          <p
            onClick={(e) => {
              if (stopPropagation) {
                e.stopPropagation();
              }
              handleExpandToggle(false);
            }}
            className={cx("mt-2", clickableText, textClassName)}
          >
            {collapsedText}
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default CollapsibleText;
